import React from 'react';
import { cn } from '../../design-system/cn';
import {
  motion,
  typography,
  touchTarget,
  consoleMotion,
  consoleShadows,
  consoleRadii,
} from '../../design-system/tokens';
import Card from '../ui/Card';
import HotActivityBadges from './HotActivityBadges';
import MatchupSportBadge from './MatchupSportBadge';
import SportEmblemDisplay from '../common/SportEmblemDisplay';
import { buildMatchupViewModel } from '../../utils/matchupViewModel';
import { useMatchupCountdown } from '../../hooks/useMatchupCountdown';
import { getSportStripeGradient } from '../../utils/gameTicketStyles';

/** Fixed footprint so every card in the rail shares one height. */
const CARD_HEIGHT = 'h-[22.75rem]';
const CARD_HEIGHT_MOBILE_BOARD = 'h-[20.5rem]';

/**
 * Carousel matchup card — team vs team is the focus; ghost sport mark stays behind.
 * Centered team names keep both sides readable without crest/initial bubbles.
 * `deck` = swipe-modal layout: auto height.
 */
export default function LobbyMatchupCard({
  event,
  onLockIn,
  onSendTicket,
  openLockCount = 0,
  groupLockCount = 0,
  featured = false,
  boardMobile = false,
  deck = false,
  className,
}) {
  const vm = buildMatchupViewModel(event);
  const startsIn = useMatchupCountdown(vm.startTime);
  const hasActivity = openLockCount > 0 || groupLockCount > 0;
  const stripe = getSportStripeGradient(event.sport);
  const hasTicket = typeof onSendTicket === 'function';

  return (
    <Card
      as="article"
      variant="premiumGlass"
      padding="none"
      className={cn(
        'group relative flex flex-col w-full overflow-hidden',
        deck
          ? 'h-auto min-h-0'
          : boardMobile
            ? CARD_HEIGHT_MOBILE_BOARD
            : CARD_HEIGHT,
        !boardMobile && !deck && 'lg:w-[280px]',
        'ring-1 ring-white/[0.08]',
        consoleRadii.md,
        motion.transition,
        motion.duration.fast,
        motion.hover,
        consoleMotion.lift,
        consoleShadows.card,
        'motion-safe:md:hover:ring-[#8cff4a]/25',
        'motion-safe:md:hover:shadow-[0_0_36px_rgba(140,255,74,0.22)]',
        vm.accent.border,
        featured ? vm.accent.glow : 'shadow-[0_0_24px_rgba(140,255,74,0.1)]',
        vm.accent.ring,
        className,
      )}
      data-testid="lobby-matchup-card"
    >
      <div
        className={cn('absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b opacity-90', stripe)}
        aria-hidden
      />

      {/* Ghost sport mark — large branded watermark only */}
      <div
        className="absolute inset-0 pointer-events-none overflow-hidden"
        aria-hidden
        data-testid="lobby-card-ghost-emblem"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(70% 55% at 78% 18%, rgba(140,255,74,0.12), transparent 55%), radial-gradient(60% 50% at 20% 90%, rgba(51,214,255,0.08), transparent 60%)',
          }}
        />
        <SportEmblemDisplay
          iconId={vm.sportIconId}
          size="hero"
          className={cn(
            'absolute -right-8 bottom-16 opacity-[0.14]',
            'scale-[1.2] rotate-[-10deg]',
            'motion-safe:transition-transform motion-safe:duration-500',
            'motion-safe:md:group-hover:scale-[1.28] motion-safe:md:group-hover:opacity-[0.2]',
          )}
          style={{ filter: 'drop-shadow(0 18px 40px rgba(0,0,0,0.55))' }}
        />
      </div>

      <div className={cn('relative z-10 flex flex-col h-full min-h-0', boardMobile || deck ? 'p-3.5' : 'p-4')}>
        <div className="flex items-center justify-between gap-2 shrink-0 mb-2">
          {featured ? (
            <div className="flex items-center gap-2 min-w-0">
              <SportEmblemDisplay iconId={vm.sportIconId} size="sm" className="!h-7 !w-7" />
              <span className={cn(typography.micro, vm.accent.label, 'truncate')}>
                {vm.sportLabel}
              </span>
            </div>
          ) : (
            <MatchupSportBadge vm={vm} className="px-2.5 py-1" />
          )}
          {featured && !boardMobile && !deck && (
            <span className={cn(typography.micro, 'text-amber-400/90')}>Featured</span>
          )}
        </div>

        {/* Team vs team — centered names, no crest bubbles */}
        <div
          className={cn(
            'relative flex flex-col items-center justify-center px-1 gap-2 text-center',
            deck ? 'shrink-0 py-2' : 'flex-1 min-h-0 py-1',
          )}
          data-testid="lobby-matchup-teams"
        >
          <p className="w-full text-sm font-black text-white leading-tight line-clamp-2 uppercase tracking-tight">
            {vm.home.name}
          </p>
          <p className={cn(typography.vsDivider, 'text-gray-500 shrink-0')}>vs</p>
          <p className="w-full text-sm font-black text-white leading-tight line-clamp-2 uppercase tracking-tight">
            {vm.away.name}
          </p>
        </div>

        <div
          className={cn(
            'relative z-20 shrink-0 mt-2 pt-3 border-t border-white/[0.08] text-center space-y-1',
            'bg-gradient-to-t from-[#141923] via-[#141923]/95 to-transparent',
          )}
        >
          <p className={cn(typography.stat, 'text-base text-[#ffc857] tabular-nums')}>{startsIn}</p>
          {vm.date && (
            <p className={cn(typography.microWide, 'text-gray-500 tabular-nums')}>{vm.dateLine}</p>
          )}
          <div className="min-h-[1rem] flex items-center justify-center">
            {hasActivity ? (
              <HotActivityBadges
                openLockCount={openLockCount}
                groupLockCount={groupLockCount}
                size="md"
              />
            ) : (
              !boardMobile && (
                <p className={cn(typography.micro, 'text-[#8cff4a]/70')}>
                  Be the first to lock in
                </p>
              )
            )}
          </div>
        </div>

        <div className={cn('relative z-20 shrink-0 mt-2.5 flex flex-col', hasTicket ? 'gap-2' : 'gap-0')}>
          <button
            type="button"
            data-tour-id="lock-it-in-button"
            onClick={() => onLockIn?.(event)}
            className={cn(
              'w-full bg-[#8cff4a] hover:brightness-110 text-[#07090d]',
              touchTarget.comfortable,
              'rounded-xl font-black text-[11px] uppercase tracking-widest',
              motion.transition,
              motion.duration.fast,
              motion.activeScale,
              consoleMotion.focus,
              consoleShadows.glow,
              'shadow-[0_8px_28px_rgba(140,255,74,0.35)]',
              'motion-safe:md:group-hover:shadow-[0_12px_36px_rgba(140,255,74,0.45)]',
            )}
          >
            {vm.cta.primary}
          </button>
          {hasTicket ? (
            <button
              type="button"
              data-tour-id="send-ticket-button"
              onClick={() => onSendTicket(event)}
              className={cn(
                'w-full border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-[#f7f9fc]',
                touchTarget.min,
                'rounded-xl font-black text-[9px] uppercase tracking-widest',
                motion.transition,
                motion.duration.fast,
                motion.activeScale,
                consoleMotion.focus,
              )}
            >
              {vm.cta.secondary}
            </button>
          ) : (
            <div className="h-11" aria-hidden />
          )}
        </div>
      </div>
    </Card>
  );
}
